import React, { useState } from "react";
import { Input, Button, message, Spin, Alert } from "antd";
import { GiftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../Constants";

const CouponValidator = ({ onCouponApplied, type = "any", productType = null, placeholder }) => {
  const { t } = useTranslation();
  const [couponCode, setCouponCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [error, setError] = useState("");

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setError(t("couponValidator.errors.codeRequired"));
      return;
    }

    setLoading(true);
    setError("");

    try {

      // If not found in samples, try API
      const response = await axios.post(`${BASE_URL}/Coupon/validate`, {
        code: couponCode,
        type: type,
        // Only sent when a caller scopes validation to a product (e.g. "digital-card").
        ...(productType ? { productType } : {}),
      });
      
      if (response.status === 200 && response.data.isValid) {
        const couponData = response.data.coupon;
        
        // Check if coupon type matches the required type
        if (type !== "any" && couponData.type !== type) {
          setError(t("couponValidator.errors.wrongType", { type: couponData.type }));
          setLoading(false);
          return;
        }

        setAppliedCoupon(couponData);
        if (onCouponApplied) {
          onCouponApplied(couponData);
        }
        message.success(t("couponValidator.appliedSuccess"));
      } else {
        setError(t("couponValidator.errors.invalid"));
      }
    } catch (error) {
      console.error("Coupon validation error:", error);
      setError(t("couponValidator.errors.validateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setError("");
    if (onCouponApplied) {
      onCouponApplied(null);
    }
  };

  const getCouponDisplay = (coupon) => {
    if (coupon.type === "coupon") {
      return t("couponValidator.display.trialDays", { days: coupon.trialDurationDays });
    }
    if (coupon.discountType === "percentage") {
      return t("couponValidator.display.percentageDiscount", { value: coupon.discountValue });
    }
    return t("couponValidator.display.amountDiscount", { value: coupon.discountValue });
  };

  if (appliedCoupon) {
    return (
      <Alert
        message={t("couponValidator.couponApplied")}
        description={
          <div>
            <div>
              <strong>{appliedCoupon.code}</strong> - {appliedCoupon.name}
            </div>
            <div>{getCouponDisplay(appliedCoupon)}</div>
          </div>
        }
        type="success"
        icon={<CheckCircleOutlined />}
        action={
          <Button size="small" onClick={removeCoupon}>
            {t("couponValidator.remove")}
          </Button>
        }
        showIcon
        className="mb-4"
      />
    );
  }

  return (
    <div className="mb-4">
      <div className="flex gap-2">
        <Input
          placeholder={placeholder || t("couponValidator.defaultPlaceholder")}
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          prefix={<GiftOutlined />}
          onPressEnter={validateCoupon}
          disabled={loading}
        />
        <Button
          type="primary"
          color="white"
          className="text-white disabled:text-white"
          onClick={validateCoupon}
          loading={loading}
          disabled={!couponCode.trim()}
        >
          {t("couponValidator.apply")}
        </Button>
      </div>
      {error && (
        <div className="mt-2">
          <Alert message={error} type="error" showIcon size="small" />
        </div>
      )}
    </div>
  );
};

export default CouponValidator;
